import { useState, useEffect } from 'react';
import { useStore } from '../context/StoreContext';
import { Icon } from '../shared/ui/Icon';
import { formatPrice, getProductPrice, safeImage, categoryName } from '../entities/product/helpers';
import { LOW_STOCK_THRESHOLD } from '../shared/config/constants';

export function AdminPage() {
  const {
    user, adminTab, setAdminTab, go, products, adminOrders, adminUsers,
    adminLoading, fetchAdminData, adminCreateProduct, adminUpdateProduct,
    adminDeleteProduct, adminUpdateOrderStatus, adminUpdateSettings,
    adminUploadImage, settings, setNotice, adminBanners,
    adminCreateBanner, adminUpdateBanner, adminDeleteBanner,
  } = useStore();

  // Load admin data on mount
  useEffect(() => {
    fetchAdminData();
  }, [fetchAdminData]);

  /* ─── Local State for Product Modal ─── */
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    title: '',
    category: '',
    brand: '',
    price: '',
    salePrice: '',
    salePercentage: '',
    stock: '',
    description: '',
    images: [],
    specs: [{ label: '', value: '' }],
    ganvadebaLink: '',
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [editingBannerId, setEditingBannerId] = useState(null);
  const [bannerForm, setBannerForm] = useState({
    order: 0, eyebrow: '', titleLine1: '', titleLine2: '', subtitle: '',
    ctaLabel: '', ctaUrl: '', imageUrl: '', imageAlt: '', isActive: true,
  });

  /* ─── Local State for Order Filter & Detail Modal ─── */
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [orderSearch, setOrderSearch] = useState('');
  const [viewingOrder, setViewingOrder] = useState(null);

  /* ─── Local State for Product Search & Filter ─── */
  const [productSearch, setProductSearch] = useState('');
  const [productCategoryFilter, setProductCategoryFilter] = useState('all');

  /* ─── Local State for Settings Form ─── */
  const [settingsForm, setSettingsForm] = useState({
    brandNameEn: '',
    brandNameKa: '',
    heroTitleEn: '',
    heroSubtitleEn: '',
    heroButtonEn: '',
    onSaleTitleEn: '',
    featuredTitleEn: '',
    contactPhone: '',
    contactEmail: '',
    contactAddress: '',
    publicFooterText: '',
    deliveryAreasJson: '[]',
    enabledPaymentMethods: [],
  });

  // Sync settings when loaded
  useEffect(() => {
    if (settings) {
      setSettingsForm({
        brandNameEn: settings.text?.brandName?.en || (typeof settings.text?.brandName === 'string' ? settings.text.brandName : ''),
        brandNameKa: settings.text?.brandName?.ka || '',
        heroTitleEn: settings.text?.home?.heroTitle?.en || settings.text?.home?.heroTitle || '',
        heroSubtitleEn: settings.text?.home?.heroSubtitle?.en || settings.text?.home?.heroSubtitle || '',
        heroButtonEn: settings.text?.home?.heroButtonText?.en || settings.text?.home?.heroButtonText || '',
        onSaleTitleEn: settings.text?.home?.onSaleTitle?.en || settings.text?.home?.onSaleTitle || '',
        featuredTitleEn: settings.text?.home?.featuredTitle?.en || settings.text?.home?.featuredTitle || '',
        contactPhone: settings.contact?.phone || settings.contactPhone || '',
        contactEmail: settings.contact?.email || '',
        contactAddress: settings.contact?.address || '',
        publicFooterText: settings.text?.publicFooterText?.en || settings.text?.publicFooterText || '',
        deliveryAreasJson: JSON.stringify(settings.deliveryAreas || [], null, 2),
        enabledPaymentMethods: (settings.enabledPaymentMethods || settings.paymentMethods || []).map((method) => (
          typeof method === 'string' ? method : method.id || method.value
        )).filter(Boolean),
      });
    }
  }, [settings]);

  /* ─── Guard for Non-Admin ─── */
  if (user?.role !== 'admin') {
    return (
      <div className="admin-access-denied">
        <span className="admin-lock-icon">🔒</span>
        <h2>Admin Access Required</h2>
        <p>You must be signed in as an administrator to access the Technomax Management Suite.</p>
        <div className="access-denied-actions">
          <button className="button button-primary" onClick={() => go('home')}>
            Return to Store <Icon>↗</Icon>
          </button>
        </div>
      </div>
    );
  }

  /* ─── Metrics Calculations ─── */
  const totalRevenue = adminOrders.reduce((sum, order) => {
    if (order.status !== 'cancelled') return sum + Number(order.total || 0);
    return sum;
  }, 0);
  const pendingOrdersCount = adminOrders.filter((o) => o.status === 'pending').length;
  const lowStockProducts = products.filter((p) => Number(p.stock || 0) <= LOW_STOCK_THRESHOLD);

  /* ─── Filtered Products ─── */
  const filteredProducts = products.filter((p) => {
    const matchesSearch = !productSearch.trim() ||
      `${p.title || p.name || ''} ${p.brand || ''} ${p.category || ''}`
        .toLowerCase()
        .includes(productSearch.trim().toLowerCase());
    const matchesCategory =
      productCategoryFilter === 'all' ||
      String(p.category || '').toLowerCase() === productCategoryFilter.toLowerCase();
    return matchesSearch && matchesCategory;
  });

  /* ─── Filtered Orders ─── */
  const filteredOrders = adminOrders.filter((order) => {
    const matchesFilter =
      orderStatusFilter === 'all' || String(order.status).toLowerCase() === orderStatusFilter.toLowerCase();
    const q = orderSearch.trim().toLowerCase();
    const matchesSearch =
      !q ||
      String(order.id).toLowerCase().includes(q) ||
      String(order.shippingAddress?.name || '').toLowerCase().includes(q) ||
      String(order.shippingAddress?.phone || '').toLowerCase().includes(q) ||
      String(order.shippingAddress?.email || '').toLowerCase().includes(q);
    return matchesFilter && matchesSearch;
  });

  /* ─── Modal Openers ─── */
  function openAddProductModal() {
    setEditingProduct(null);
    setProductForm({
      title: '',
      category: '',
      brand: '',
      price: '',
      salePrice: '',
      salePercentage: '',
      stock: '',
      description: '',
      images: [],
      specs: [{ label: '', value: '' }],
      ganvadebaLink: '',
    });
    setNewImageUrl('');
    setProductModalOpen(true);
  }

  function openEditProductModal(p) {
    setEditingProduct(p);
    setProductForm({
      title: p.title || p.name || '',
      category: p.category || '',
      brand: p.brand || '',
      price: p.price ?? '',
      salePrice: p.salePrice ?? '',
      salePercentage: p.salePercentage ?? '',
      stock: p.stock ?? '',
      description: p.description || '',
      images: Array.isArray(p.images) ? [...p.images] : (p.image ? [p.image] : []),
      specs: (p.specs && p.specs.length) ? p.specs : [{ label: '', value: '' }],
      ganvadebaLink: p.ganvadebaLink || '',
    });
    setNewImageUrl('');
    setProductModalOpen(true);
  }

  /* ─── Product Image Upload Handler ─── */
  async function handleImageFileUpload(e) {
    const files = [...(e.target.files || [])];
    if (!files.length) return;
    setUploadingImage(true);
    try {
      const urls = await Promise.all(files.map((file) => adminUploadImage(file)));
      setProductForm((prev) => ({
        ...prev,
        images: [...prev.images, ...urls],
      }));
      setNotice(`${urls.length} image${urls.length === 1 ? '' : 's'} uploaded successfully`);
    } catch (err) {
      setNotice(`Upload failed: ${err.message}`);
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  }

  async function handleBannerImageUpload(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const imageUrl = await adminUploadImage(file);
      setBannerForm((current) => ({ ...current, imageUrl, imageAlt: current.imageAlt || file.name }));
    } catch (error) {
      setNotice(`Banner image upload failed: ${error.message}`);
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  }

  function editBanner(banner) {
    setEditingBannerId(banner.id);
    setBannerForm({
      order: banner.order,
      eyebrow: banner.eyebrow || '',
      titleLine1: banner.titleLine1 || '',
      titleLine2: banner.titleLine2 || '',
      subtitle: banner.subtitle || '',
      ctaLabel: banner.ctaLabel || '',
      ctaUrl: banner.ctaUrl || '',
      imageUrl: banner.imageUrl || '',
      imageAlt: banner.imageAlt || '',
      isActive: banner.isActive,
    });
  }

  async function saveBanner(event) {
    event.preventDefault();
    try {
      if (editingBannerId) await adminUpdateBanner(editingBannerId, bannerForm);
      else await adminCreateBanner(bannerForm);
      setEditingBannerId(null);
      setBannerForm({
        order: adminBanners.length, eyebrow: '', titleLine1: '', titleLine2: '',
        subtitle: '', ctaLabel: '', ctaUrl: '', imageUrl: '', imageAlt: '', isActive: true,
      });
      setNotice('Hero slide saved');
    } catch (error) {
      setNotice(`Could not save hero slide: ${error.message}`);
    }
  }

  async function removeBanner(id) {
    try {
      await adminDeleteBanner(id);
      if (editingBannerId === id) setEditingBannerId(null);
      setNotice('Hero slide deleted');
    } catch (error) {
      setNotice(`Could not delete hero slide: ${error.message}`);
    }
  }

  function handleAddImageUrl() {
    if (!newImageUrl.trim()) return;
    setProductForm((prev) => ({
      ...prev,
      images: [...prev.images, newImageUrl.trim()],
    }));
    setNewImageUrl('');
  }

  function handleRemoveImage(index) {
    setProductForm((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  }

  /* ─── Specs Builder ─── */
  function handleSpecChange(index, field, value) {
    setProductForm((prev) => {
      const updated = [...prev.specs];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, specs: updated };
    });
  }

  function handleAddSpecRow() {
    setProductForm((prev) => ({
      ...prev,
      specs: [...prev.specs, { label: '', value: '' }],
    }));
  }

  function handleRemoveSpecRow(index) {
    setProductForm((prev) => ({
      ...prev,
      specs: prev.specs.filter((_, i) => i !== index),
    }));
  }

  /* ─── Submit Product ─── */
  async function handleSaveProduct(e) {
    e.preventDefault();
    const payload = {
      title: productForm.title.trim(),
      category: productForm.category.trim().toLowerCase(),
      brand: productForm.brand.trim(),
      price: Number(productForm.price),
      salePrice: productForm.salePrice ? Number(productForm.salePrice) : null,
      salePercentage: productForm.salePercentage ? Number(productForm.salePercentage) : 0,
      stock: Number(productForm.stock || 0),
      description: productForm.description.trim(),
      images: productForm.images,
      specs: productForm.specs.filter((s) => s.label && s.value),
      ganvadebaLink: productForm.ganvadebaLink.trim(),
    };

    try {
      if (editingProduct) {
        await adminUpdateProduct(editingProduct.id, payload);
      } else {
        await adminCreateProduct(payload);
      }
      setProductModalOpen(false);
    } catch {
      // Notice handled in StoreContext
    }
  }

  async function handleDeleteProduct(p) {
    if (window.confirm(`Are you sure you want to delete "${p.title || p.name}"?`)) {
      try {
        await adminDeleteProduct(p.id);
      } catch {
        // Notice handled in StoreContext
      }
    }
  }

  /* ─── Save Settings ─── */
  async function handleSaveSettings(e) {
    e.preventDefault();
    let deliveryAreas;
    try {
      deliveryAreas = JSON.parse(settingsForm.deliveryAreasJson);
      if (!Array.isArray(deliveryAreas) || deliveryAreas.some((area) => !area.slug || !area.name || !Number.isFinite(Number(area.fee)) || Number(area.fee) < 0)) {
        throw new Error('Each delivery area needs a slug, name, and non-negative fee.');
      }
    } catch (error) {
      setNotice(`Delivery settings are invalid: ${error.message}`);
      return;
    }
    const patch = {
      text: {
        ...(settings?.text || {}),
        brandName: {
          en: settingsForm.brandNameEn,
          ka: settingsForm.brandNameKa,
        },
        publicFooterText: {
          en: settingsForm.publicFooterText,
          ka: settingsForm.publicFooterText,
        },
        home: {
          ...(settings?.text?.home || {}),
          heroTitle: { en: settingsForm.heroTitleEn },
          heroSubtitle: { en: settingsForm.heroSubtitleEn },
          heroButtonText: { en: settingsForm.heroButtonEn },
          onSaleTitle: { en: settingsForm.onSaleTitleEn },
          featuredTitle: { en: settingsForm.featuredTitleEn },
        },
      },
      contact: {
        ...(settings?.contact || {}),
        phone: settingsForm.contactPhone,
        email: settingsForm.contactEmail,
        address: settingsForm.contactAddress,
      },
      deliveryAreas,
      enabledPaymentMethods: settingsForm.enabledPaymentMethods,
    };
    await adminUpdateSettings(patch);
  }

  return (
    <div className="admin-page">
      {/* ─── Admin Top Bar ─── */}
      <header className="admin-header">
        <div className="admin-header-brand">
          <div className="admin-badge">ADMIN CONTROL SUITE</div>
          <h1>{typeof settings?.text?.brandName === 'string' ? settings.text.brandName : settings?.text?.brandName?.en || ''}</h1>
        </div>

        <nav className="admin-nav-tabs">
          <button
            className={adminTab === 'overview' ? 'active' : ''}
            onClick={() => setAdminTab('overview')}
          >
            📊 Overview
          </button>
          <button
            className={adminTab === 'products' ? 'active' : ''}
            onClick={() => setAdminTab('products')}
          >
            📦 Products ({products.length})
          </button>
          <button
            className={adminTab === 'orders' ? 'active' : ''}
            onClick={() => setAdminTab('orders')}
          >
            📑 Orders ({adminOrders.length})
            {pendingOrdersCount > 0 && <span className="tab-pill">{pendingOrdersCount}</span>}
          </button>
          <button
            className={adminTab === 'users' ? 'active' : ''}
            onClick={() => setAdminTab('users')}
          >
            👥 Users ({adminUsers.length})
          </button>
          <button
            className={adminTab === 'settings' ? 'active' : ''}
            onClick={() => setAdminTab('settings')}
          >
            ⚙️ Storefront Settings
          </button>
        </nav>

        <div className="admin-header-actions">
          <button className="button button-outline admin-store-btn" onClick={() => go('home')}>
            ← Back to Storefront
          </button>
        </div>
      </header>

      {/* ─── Main Admin Content ─── */}
      <main className="admin-content">
        {adminLoading && (
          <div className="admin-loading-bar">
            <span>Syncing live backend data...</span>
          </div>
        )}

        {/* ─── TAB 1: OVERVIEW ─── */}
        {adminTab === 'overview' && (
          <section className="admin-tab-pane">
            <div className="admin-pane-title">
              <div>
                <p className="eyebrow">STORE PERFORMANCE</p>
                <h2>Dashboard & Key Metrics</h2>
              </div>
              <button className="button button-primary" onClick={openAddProductModal}>
                ＋ Add New Product
              </button>
            </div>

            {/* Metrics Cards */}
            <div className="admin-metrics-grid">
              <div className="metric-card">
                <span className="metric-icon">💰</span>
                <div>
                  <small>Total Revenue</small>
                  <b>{formatPrice(totalRevenue)}</b>
                </div>
              </div>
              <div className="metric-card">
                <span className="metric-icon">📑</span>
                <div>
                  <small>Total Orders</small>
                  <b>{adminOrders.length}</b>
                </div>
              </div>
              <div className="metric-card">
                <span className="metric-icon">⏳</span>
                <div>
                  <small>Pending Orders</small>
                  <b>{pendingOrdersCount}</b>
                </div>
              </div>
              <div className="metric-card">
                <span className="metric-icon">📦</span>
                <div>
                  <small>Active Products</small>
                  <b>{products.length}</b>
                </div>
              </div>
              <div className="metric-card">
                <span className="metric-icon">👥</span>
                <div>
                  <small>Registered Users</small>
                  <b>{adminUsers.length}</b>
                </div>
              </div>
            </div>

            {/* Recent Orders Table & Low Stock Alert */}
            <div className="admin-two-column">
              <div className="admin-card">
                <div className="admin-card-header">
                  <h3>Recent Customer Orders</h3>
                  <button className="text-link" onClick={() => setAdminTab('orders')}>
                    View All ({adminOrders.length}) →
                  </button>
                </div>
                {adminOrders.length > 0 ? (
                  <div className="admin-table-wrapper">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Order</th>
                          <th>Customer</th>
                          <th>Total</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {adminOrders.slice(0, 6).map((order) => (
                          <tr key={order.id}>
                            <td>
                              <b>#{String(order.id).slice(-6).toUpperCase()}</b>
                              <small>{new Date(order.createdAt).toLocaleDateString()}</small>
                            </td>
                            <td>
                              <b>{order.shippingAddress?.name || 'Customer'}</b>
                              <small>{order.shippingAddress?.phone || order.shippingAddress?.email || '-'}</small>
                            </td>
                            <td><b>{formatPrice(order.total)}</b></td>
                            <td>
                              <span className={`status status-${String(order.status).toLowerCase()}`}>
                                {order.status}
                              </span>
                            </td>
                            <td>
                              <button className="text-link" onClick={() => setViewingOrder(order)}>
                                View Details ↗
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-state-mini">No orders received yet.</div>
                )}
              </div>

              {/* Low Stock Alerts */}
              <div className="admin-card">
                <div className="admin-card-header">
                  <h3>Inventory & Stock Alerts</h3>
                  <button className="text-link" onClick={() => setAdminTab('products')}>
                    Manage Inventory →
                  </button>
                </div>
                {lowStockProducts.length > 0 ? (
                  <div className="admin-low-stock-list">
                    {lowStockProducts.map((p) => (
                      <div className="low-stock-item" key={p.id}>
                        <img src={safeImage(p)} alt="" />
                        <div>
                          <b>{p.title || p.name}</b>
                          <small>{p.brand || categoryName(p.category)}</small>
                        </div>
                        <span className={`stock-pill ${Number(p.stock) === 0 ? 'out-of-stock' : 'low-stock'}`}>
                          {Number(p.stock) === 0 ? 'Out of stock' : `Only ${p.stock} left`}
                        </span>
                        <button className="text-link" onClick={() => openEditProductModal(p)}>
                          Edit Stock ↗
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state-mini">All inventory items are well-stocked!</div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* ─── TAB 2: PRODUCTS ─── */}
        {adminTab === 'products' && (
          <section className="admin-tab-pane">
            <div className="admin-pane-title">
              <div>
                <p className="eyebrow">INVENTORY CONTROL</p>
                <h2>Product Catalog Management</h2>
              </div>
              <button className="button button-primary" onClick={openAddProductModal}>
                ＋ Add New Product
              </button>
            </div>

            {/* Filter and Search Bar */}
            <div className="admin-filter-bar">
              <input
                className="admin-search-input"
                placeholder="Search products by name, brand, or category..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
              />
              <select
                className="admin-select"
                value={productCategoryFilter}
                onChange={(e) => setProductCategoryFilter(e.target.value)}
              >
                <option value="all">All Categories</option>
                {[...new Set(products.map((p) => p.category).filter(Boolean))].map((cat) => (
                  <option key={cat} value={cat}>{categoryName(cat)}</option>
                ))}
              </select>
            </div>

            {/* Products Table */}
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Category</th>
                    <th>Price</th>
                    <th>Discount</th>
                    <th>Stock</th>
                    <th>Rating</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((product) => {
                    const title = product.title || product.name;
                    const isDiscounted = Number(product.salePrice) > 0 || Number(product.salePercentage) > 0;
                    return (
                      <tr key={product.id}>
                        <td className="product-cell">
                          <img src={safeImage(product)} alt="" />
                          <div>
                            <b>{title}</b>
                            <small>{product.brand || 'Brand unassigned'}</small>
                          </div>
                        </td>
                        <td><span className="cat-pill">{categoryName(product.category)}</span></td>
                        <td>
                          <b>{formatPrice(getProductPrice(product))}</b>
                          {isDiscounted && <del>{formatPrice(product.price)}</del>}
                        </td>
                        <td>
                          {isDiscounted ? (
                            <span className="sale-pill">
                              {Number(product.salePercentage) > 0 ? `-${product.salePercentage}%` : 'Special Price'}
                            </span>
                          ) : (
                            <span className="no-sale-pill">—</span>
                          )}
                        </td>
                        <td>
                          <span className={`stock-badge ${Number(product.stock) <= 3 ? 'stock-low' : 'stock-ok'}`}>
                            {product.stock ?? 0} in stock
                          </span>
                        </td>
                        <td>{Number(product.reviewCount) > 0 ? `★ ${Number(product.rating).toFixed(1)} (${product.reviewCount})` : 'No reviews'}</td>
                        <td className="actions-cell">
                          <button
                            className="admin-action-btn edit-btn"
                            title="Edit Product"
                            onClick={() => openEditProductModal(product)}
                          >
                            ✎ Edit
                          </button>
                          <button
                            className="admin-action-btn delete-btn"
                            title="Delete Product"
                            onClick={() => handleDeleteProduct(product)}
                          >
                            🗑 Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!filteredProducts.length && (
                <div className="empty-state-mini">No products match your filters.</div>
              )}
            </div>
          </section>
        )}

        {/* ─── TAB 3: ORDERS ─── */}
        {adminTab === 'orders' && (
          <section className="admin-tab-pane">
            <div className="admin-pane-title">
              <div>
                <p className="eyebrow">ORDER FULFILLMENT</p>
                <h2>Customer Orders Management</h2>
              </div>
            </div>

            {/* Filter tabs */}
            <div className="admin-order-status-tabs">
              {['all', 'pending', 'paid', 'processing', 'shipped', 'delivered', 'cancelled'].map((st) => (
                <button
                  key={st}
                  className={`status-tab ${orderStatusFilter === st ? 'active' : ''}`}
                  onClick={() => setOrderStatusFilter(st)}
                >
                  {st.toUpperCase()}
                  <span>
                    {st === 'all'
                      ? adminOrders.length
                      : adminOrders.filter((o) => String(o.status).toLowerCase() === st).length}
                  </span>
                </button>
              ))}
            </div>

            {/* Search Input */}
            <div className="admin-filter-bar">
              <input
                className="admin-search-input"
                placeholder="Search orders by ID, customer name, phone, or email..."
                value={orderSearch}
                onChange={(e) => setOrderSearch(e.target.value)}
              />
            </div>

            {/* Orders Table */}
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Customer</th>
                    <th>Location</th>
                    <th>Items</th>
                    <th>Payment</th>
                    <th>Total</th>
                    <th>Status Selector</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order) => (
                    <tr key={order.id}>
                      <td>
                        <b>#{String(order.id).slice(-8).toUpperCase()}</b>
                        <small>{new Date(order.createdAt).toLocaleString()}</small>
                      </td>
                      <td>
                        <b>{order.shippingAddress?.name || 'Customer'}</b>
                        <small>{order.shippingAddress?.phone || order.shippingAddress?.email || '-'}</small>
                      </td>
                      <td>
                        <b>{order.shippingAddress?.city || 'Tbilisi'}</b>
                        <small>{order.deliveryLocation || order.shippingAddress?.address || '-'}</small>
                      </td>
                      <td>
                        <b>{(order.items || []).length} items</b>
                        <small>{(order.items || []).map((i) => i.title || i.name).slice(0, 2).join(', ')}</small>
                      </td>
                      <td>
                        <span className="payment-method-pill">{order.paymentMethod || 'Card'}</span>
                      </td>
                      <td><strong>{formatPrice(order.total)}</strong></td>
                      <td>
                        <select
                          className={`status-select status-${String(order.status).toLowerCase()}`}
                          value={order.status || 'pending'}
                          onChange={(e) => adminUpdateOrderStatus(order.id, e.target.value)}
                        >
                          <option value="pending">Pending</option>
                          <option value="paid">Paid</option>
                          <option value="processing">Processing</option>
                          <option value="shipped">Shipped</option>
                          <option value="delivered">Delivered</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </td>
                      <td>
                        <button className="text-link" onClick={() => setViewingOrder(order)}>
                          View Receipt ↗
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!filteredOrders.length && (
                <div className="empty-state-mini">No orders found matching criteria.</div>
              )}
            </div>
          </section>
        )}

        {/* ─── TAB 4: USERS ─── */}
        {adminTab === 'users' && (
          <section className="admin-tab-pane">
            <div className="admin-pane-title">
              <div>
                <p className="eyebrow">ACCOUNTS</p>
                <h2>Registered Users ({adminUsers.length})</h2>
              </div>
            </div>

            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {adminUsers.map((u) => (
                    <tr key={u.id}>
                      <td className="user-cell">
                        <span className="user-avatar">{u.name?.[0]?.toUpperCase() || 'U'}</span>
                        <b>{u.name || 'Anonymous User'}</b>
                      </td>
                      <td>{u.email}</td>
                      <td>
                        <span className={`role-badge ${u.role === 'admin' ? 'role-admin' : 'role-customer'}`}>
                          {u.role === 'admin' ? '👑 Admin' : 'Customer'}
                        </span>
                      </td>
                      <td><small>{new Date(u.createdAt).toLocaleDateString()}</small></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!adminUsers.length && (
                <div className="empty-state-mini">No registered users found.</div>
              )}
            </div>
          </section>
        )}

        {/* ─── TAB 5: SETTINGS ─── */}
        {adminTab === 'settings' && (
          <section className="admin-tab-pane">
            <div className="admin-pane-title">
              <div>
                <p className="eyebrow">CONFIGURATION</p>
                <h2>Storefront Content & Settings</h2>
              </div>
            </div>

            <form className="admin-settings-form" onSubmit={handleSaveSettings}>
              <div className="admin-settings-section">
                <h3>Brand Identity</h3>
                <div className="form-grid">
                  <label>
                    Brand Name (English)
                    <input
                      value={settingsForm.brandNameEn}
                      onChange={(e) => setSettingsForm({ ...settingsForm, brandNameEn: e.target.value })}
                      required
                    />
                  </label>
                  <label>
                    Brand Name (Georgian)
                    <input
                      value={settingsForm.brandNameKa}
                      onChange={(e) => setSettingsForm({ ...settingsForm, brandNameKa: e.target.value })}
                    />
                  </label>
                  <label>
                    Contact Phone Number
                    <input
                      value={settingsForm.contactPhone}
                      onChange={(e) => setSettingsForm({ ...settingsForm, contactPhone: e.target.value })}
                    />
                  </label>
                  <label>
                    Contact Email
                    <input
                      type="email"
                      value={settingsForm.contactEmail}
                      onChange={(e) => setSettingsForm({ ...settingsForm, contactEmail: e.target.value })}
                    />
                  </label>
                  <label>
                    Contact Address
                    <input
                      value={settingsForm.contactAddress}
                      onChange={(e) => setSettingsForm({ ...settingsForm, contactAddress: e.target.value })}
                    />
                  </label>
                  <label>
                    Footer Copyright Text
                    <input
                      value={settingsForm.publicFooterText}
                      onChange={(e) => setSettingsForm({ ...settingsForm, publicFooterText: e.target.value })}
                    />
                  </label>
                </div>
              </div>

              <div className="admin-settings-section">
                <h3>Homepage Hero Banner</h3>
                <div className="form-grid">
                  <label>
                    Hero Title
                    <input
                      value={settingsForm.heroTitleEn}
                      onChange={(e) => setSettingsForm({ ...settingsForm, heroTitleEn: e.target.value })}
                    />
                  </label>
                  <label>
                    Hero Subtitle
                    <input
                      value={settingsForm.heroSubtitleEn}
                      onChange={(e) => setSettingsForm({ ...settingsForm, heroSubtitleEn: e.target.value })}
                    />
                  </label>
                  <label>
                    Hero Button Text
                    <input
                      value={settingsForm.heroButtonEn}
                      onChange={(e) => setSettingsForm({ ...settingsForm, heroButtonEn: e.target.value })}
                    />
                  </label>
                  <label>
                    Featured Section Title
                    <input
                      value={settingsForm.featuredTitleEn}
                      onChange={(e) => setSettingsForm({ ...settingsForm, featuredTitleEn: e.target.value })}
                    />
                  </label>
                </div>
              </div>

              <div className="admin-settings-section">
                <h3>Delivery and payment configuration</h3>
                <label className="full-span">
                  Delivery areas (JSON objects with slug, name, and fee)
                  <textarea
                    rows={6}
                    className="admin-textarea"
                    value={settingsForm.deliveryAreasJson}
                    onChange={(event) => setSettingsForm({ ...settingsForm, deliveryAreasJson: event.target.value })}
                  />
                </label>
                <div className="admin-payment-methods">
                  {[
                    ['card', 'Card payment'],
                    ['installment-ganvadeba', 'Installment payment'],
                    ['cod', 'Cash on delivery'],
                  ].map(([value, label]) => (
                    <label key={value}>
                      <input
                        type="checkbox"
                        checked={settingsForm.enabledPaymentMethods.includes(value)}
                        onChange={(event) => setSettingsForm((current) => ({
                          ...current,
                          enabledPaymentMethods: event.target.checked
                            ? [...current.enabledPaymentMethods, value]
                            : current.enabledPaymentMethods.filter((method) => method !== value),
                        }))}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="admin-settings-footer">
                <button className="button button-primary" type="submit">
                  Save All Settings <Icon>→</Icon>
                </button>
              </div>
            </form>

            <section className="admin-settings-section admin-banner-manager">
              <div className="admin-pane-title">
                <div>
                  <h3>Hero slides</h3>
                  <p>Slides are published from the API in display order.</p>
                </div>
              </div>
              <div className="admin-banner-list">
                {adminBanners.map((banner) => (
                  <article className="admin-banner-row" key={banner.id}>
                    {banner.imageUrl && <img src={banner.imageUrl} alt={banner.imageAlt || ''} />}
                    <div><b>{banner.titleLine1} {banner.titleLine2}</b><small>Order {banner.order} · {banner.isActive ? 'Active' : 'Hidden'}</small></div>
                    <button type="button" className="button button-outline" onClick={() => editBanner(banner)}>Edit</button>
                    <button type="button" className="button button-outline" onClick={() => removeBanner(banner.id)}>Delete</button>
                  </article>
                ))}
                {!adminBanners.length && <p>No hero slides have been configured.</p>}
              </div>
              <form className="admin-settings-form" onSubmit={saveBanner}>
                <div className="form-grid">
                  <label>Display order<input type="number" min="0" value={bannerForm.order} onChange={(event) => setBannerForm({ ...bannerForm, order: Number(event.target.value) })} /></label>
                  <label>Eyebrow<input value={bannerForm.eyebrow} onChange={(event) => setBannerForm({ ...bannerForm, eyebrow: event.target.value })} /></label>
                  <label>Title line 1<input required value={bannerForm.titleLine1} onChange={(event) => setBannerForm({ ...bannerForm, titleLine1: event.target.value })} /></label>
                  <label>Title line 2<input value={bannerForm.titleLine2} onChange={(event) => setBannerForm({ ...bannerForm, titleLine2: event.target.value })} /></label>
                  <label className="full-span">Subtitle<textarea rows={2} value={bannerForm.subtitle} onChange={(event) => setBannerForm({ ...bannerForm, subtitle: event.target.value })} /></label>
                  <label>Button label<input value={bannerForm.ctaLabel} onChange={(event) => setBannerForm({ ...bannerForm, ctaLabel: event.target.value })} /></label>
                  <label>Button destination<input required placeholder="/products/slug or https://..." value={bannerForm.ctaUrl} onChange={(event) => setBannerForm({ ...bannerForm, ctaUrl: event.target.value })} /></label>
                  <label className="full-span">Image URL<input required value={bannerForm.imageUrl} onChange={(event) => setBannerForm({ ...bannerForm, imageUrl: event.target.value })} /></label>
                  <label className="full-span">Upload image<input type="file" accept="image/*" disabled={uploadingImage} onChange={handleBannerImageUpload} /></label>
                  <label className="full-span">Image description<input value={bannerForm.imageAlt} onChange={(event) => setBannerForm({ ...bannerForm, imageAlt: event.target.value })} /></label>
                  <label className="admin-checkbox"><input type="checkbox" checked={bannerForm.isActive} onChange={(event) => setBannerForm({ ...bannerForm, isActive: event.target.checked })} /> Active</label>
                </div>
                <div className="admin-settings-footer">
                  <button className="button button-primary" type="submit">{editingBannerId ? 'Update hero slide' : 'Add hero slide'} <Icon>→</Icon></button>
                  {editingBannerId && <button className="button button-outline" type="button" onClick={() => { setEditingBannerId(null); setBannerForm({ order: adminBanners.length, eyebrow: '', titleLine1: '', titleLine2: '', subtitle: '', ctaLabel: '', ctaUrl: '', imageUrl: '', imageAlt: '', isActive: true }); }}>Cancel editing</button>}
                </div>
              </form>
            </section>
          </section>
        )}
      </main>

      {/* ─── ADD / EDIT PRODUCT MODAL ─── */}
      {productModalOpen && (
        <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) setProductModalOpen(false); }}>
          <div className="admin-modal product-modal">
            <button className="modal-close" onClick={() => setProductModalOpen(false)}>×</button>
            <p className="eyebrow">{editingProduct ? 'EDIT EXISTING' : 'CREATE NEW'}</p>
            <h2>{editingProduct ? `Edit ${editingProduct.title || editingProduct.name}` : 'Add New Product'}</h2>

            <form onSubmit={handleSaveProduct}>
              <div className="form-grid">
                <label className="full-span">
                  Product Title *
                  <input
                    required
                    placeholder="e.g. Sony WH-1000XM5 Wireless Headphones"
                    value={productForm.title}
                    onChange={(e) => setProductForm({ ...productForm, title: e.target.value })}
                  />
                </label>

                <label>
                  Category *
                  <input
                    required
                    placeholder="e.g. audio, phones, laptops, tv"
                    value={productForm.category}
                    onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                  />
                </label>

                <label>
                  Brand
                  <input
                    placeholder="e.g. Apple, Samsung, Sony"
                    value={productForm.brand}
                    onChange={(e) => setProductForm({ ...productForm, brand: e.target.value })}
                  />
                </label>

                <label>
                  Regular Price (₾) *
                  <input
                    type="number"
                    step="any"
                    required
                    placeholder="999"
                    value={productForm.price}
                    onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                  />
                </label>

                <label>
                  Sale Price (₾)
                  <input
                    type="number"
                    step="any"
                    placeholder="Leave blank if no sale"
                    value={productForm.salePrice}
                    onChange={(e) => setProductForm({ ...productForm, salePrice: e.target.value })}
                  />
                </label>

                <label>
                  Sale Percentage (%)
                  <input
                    type="number"
                    placeholder="e.g. 15"
                    value={productForm.salePercentage}
                    onChange={(e) => setProductForm({ ...productForm, salePercentage: e.target.value })}
                  />
                </label>

                <label>
                  Stock Quantity *
                  <input
                    type="number"
                    required
                    value={productForm.stock}
                    onChange={(e) => setProductForm({ ...productForm, stock: e.target.value })}
                  />
                </label>

                <label>
                  Installment (Ganvadeba) Link
                  <input
                    placeholder="https://..."
                    value={productForm.ganvadebaLink}
                    onChange={(e) => setProductForm({ ...productForm, ganvadebaLink: e.target.value })}
                  />
                </label>

                <label className="full-span">
                  Description
                  <textarea
                    rows={3}
                    className="admin-textarea"
                    placeholder="Detailed description of features, materials, warranty..."
                    value={productForm.description}
                    onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                  />
                </label>
              </div>

              {/* Product Images Management */}
              <div className="admin-modal-section">
                <h4>Product Images ({productForm.images.length})</h4>
                <div className="image-previews-grid">
                  {productForm.images.map((img, i) => (
                    <div className="image-preview-card" key={i}>
                      <img src={img} alt={`Preview ${i}`} />
                      <button type="button" onClick={() => handleRemoveImage(i)}>✕</button>
                    </div>
                  ))}
                </div>

                <div className="image-upload-controls">
                  <label className="file-upload-btn">
                    {uploadingImage ? 'Uploading image...' : '📁 Upload Image File'}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      disabled={uploadingImage}
                      onChange={handleImageFileUpload}
                      style={{ display: 'none' }}
                    />
                  </label>
                  <div className="url-upload-row">
                    <input
                      placeholder="Or paste image URL (https://...)"
                      value={newImageUrl}
                      onChange={(e) => setNewImageUrl(e.target.value)}
                    />
                    <button type="button" className="button button-outline" onClick={handleAddImageUrl}>
                      Add URL
                    </button>
                  </div>
                </div>
              </div>

              {/* Specs Builder */}
              <div className="admin-modal-section">
                <div className="specs-header">
                  <h4>Product Specifications</h4>
                  <button type="button" className="text-link" onClick={handleAddSpecRow}>
                    ＋ Add Spec Row
                  </button>
                </div>
                <div className="specs-rows">
                  {productForm.specs.map((spec, index) => (
                    <div className="spec-input-row" key={index}>
                      <input
                        placeholder="Label (e.g. CPU, RAM, Color)"
                        value={spec.label}
                        onChange={(e) => handleSpecChange(index, 'label', e.target.value)}
                      />
                      <input
                        placeholder="Value (e.g. Apple M3, 16GB, Titanium)"
                        value={spec.value}
                        onChange={(e) => handleSpecChange(index, 'value', e.target.value)}
                      />
                      <button
                        type="button"
                        className="spec-remove-btn"
                        onClick={() => handleRemoveSpecRow(index)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="button button-outline"
                  onClick={() => setProductModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="button button-primary">
                  {editingProduct ? 'Save Changes' : 'Create Product'} <Icon>→</Icon>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── ORDER RECEIPT / DETAILS MODAL ─── */}
      {viewingOrder && (
        <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) setViewingOrder(null); }}>
          <div className="admin-modal order-detail-modal">
            <button className="modal-close" onClick={() => setViewingOrder(null)}>×</button>
            <p className="eyebrow">ORDER BREAKDOWN</p>
            <h2>Order #{String(viewingOrder.id).slice(-8).toUpperCase()}</h2>
            <p className="order-date-label">Placed on {new Date(viewingOrder.createdAt).toLocaleString()}</p>

            <div className="order-modal-grid">
              {/* Customer Info */}
              <div className="order-modal-box">
                <h4>Customer & Shipping Address</h4>
                <p><b>Name:</b> {viewingOrder.shippingAddress?.name || '-'}</p>
                <p><b>Phone:</b> {viewingOrder.shippingAddress?.phone || '-'}</p>
                <p><b>Email:</b> {viewingOrder.shippingAddress?.email || '-'}</p>
                <p><b>City:</b> {viewingOrder.shippingAddress?.city || 'Tbilisi'}</p>
                <p><b>Street Address:</b> {viewingOrder.shippingAddress?.address || '-'}</p>
                <p><b>Postal Code:</b> {viewingOrder.shippingAddress?.postalCode || '-'}</p>
                <p><b>Delivery Zone:</b> {viewingOrder.deliveryLocation || 'Tbilisi'}</p>
              </div>

              {/* Status & Payment */}
              <div className="order-modal-box">
                <h4>Status & Payment</h4>
                <p><b>Payment Method:</b> {viewingOrder.paymentMethod || 'Card'}</p>
                <p><b>Payment Status:</b> {viewingOrder.paymentStatus || 'pending'}</p>
                <div className="modal-status-changer">
                  <b>Update Order Status:</b>
                  <select
                    className={`status-select status-${String(viewingOrder.status).toLowerCase()}`}
                    value={viewingOrder.status || 'pending'}
                    onChange={(e) => {
                      const newStatus = e.target.value;
                      adminUpdateOrderStatus(viewingOrder.id, newStatus);
                      setViewingOrder({ ...viewingOrder, status: newStatus });
                    }}
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Line items list */}
            <div className="order-modal-items">
              <h4>Ordered Items ({(viewingOrder.items || []).length})</h4>
              {(viewingOrder.items || []).map((item, idx) => (
                <div className="order-modal-line" key={idx}>
                  <img src={safeImage(item)} alt="" />
                  <div>
                    <b>{item.title || item.name}</b>
                    <small>Quantity: {item.qty || 1} × {formatPrice(getProductPrice(item))}</small>
                  </div>
                  <strong>{formatPrice(getProductPrice(item) * Number(item.qty || 1))}</strong>
                </div>
              ))}
              <div className="order-modal-total">
                <span>Grand Total</span>
                <strong>{formatPrice(viewingOrder.total)}</strong>
              </div>
            </div>

            <div className="modal-actions">
              <button className="button button-primary" onClick={() => setViewingOrder(null)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
